const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Types
export interface UserInfo {
  type: 'ORGANIZATION' | 'ADMIN';
  isAdmin: boolean;
  isSuperAdmin: boolean;
  organization: {
    id: number;
    name: string;
    storeId: string;
    settings: Record<string, unknown>;
  } | null;
  admin: {
    id: number;
    name: string;
  } | null;
}

export interface EffectiveSetting {
  key: string;
  value: boolean | number | string;
  type: 'boolean' | 'number' | 'string' | 'option';
  options?: string[]; // Available options for 'option' type
  description: string;
  source: 'global' | 'organization';
  isOverridden: boolean;
}

export interface Organization {
  id: number;
  name: string;
  storeId: string;
  accessCodeId: number;
  settings: Record<string, SettingValue>;
  effectiveSettings?: Record<string, EffectiveSetting>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Admin {
  id: number;
  name: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  organization: {
    id: number;
    name: string;
    storeId: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccessCode {
  id: number;
  type: 'ORGANIZATION' | 'ADMIN';
  description: string;
  isActive: boolean;
  validFrom: string | null;
  validUntil: string | null;
  maxUses: number | null;
  usageCount: number;
  organization?: {
    id: number;
    name: string;
    storeId: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface SettingValue {
  type: 'boolean' | 'number' | 'string' | 'option';
  value: boolean | number | string;
}

export interface GlobalSetting {
  id: number;
  key: string;
  value: boolean | number | string;
  type: 'boolean' | 'number' | 'string' | 'option';
  options?: string[] | null; // Available options for 'option' type
  description: string;
  updatedAt: string;
}

export interface Training {
  id: number;
  slug: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  error?: string;
  code?: string;
  message?: string;
  [key: string]: T | boolean | string | undefined;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T> & T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.error || 'API Error') as Error & { status: number; code?: string };
    error.status = response.status;
    error.code = data.code;
    throw error;
  }

  return data;
}

// Auth API
export const authApi = {
  login: async (code: string) => {
    return fetchApi<{ user: UserInfo }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  logout: async () => {
    return fetchApi('/auth/logout', {
      method: 'POST',
    });
  },

  checkSession: async () => {
    return fetchApi<{ authenticated: boolean; user: UserInfo }>('/auth/session');
  },
};

// Training API
export const trainingApi = {
  getAll: async () => {
    return fetchApi<{ trainings: Training[] }>('/trainings');
  },

  getBySlug: async (slug: string) => {
    return fetchApi<{ training: Training }>(`/trainings/${slug}`);
  },
};

// Organization API (Admin only)
export const organizationApi = {
  getAll: async () => {
    return fetchApi<{ organizations: Organization[] }>('/organizations');
  },

  getById: async (id: number) => {
    return fetchApi<{ organization: Organization }>(`/organizations/${id}`);
  },

  create: async (data: { name: string; storeId: string; settings?: Record<string, SettingValue> }) => {
    return fetchApi<{ organization: Organization; accessCode: string }>('/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: { name?: string; storeId?: string; isActive?: boolean }) => {
    return fetchApi<{ organization: Organization }>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: number) => {
    return fetchApi(`/organizations/${id}`, {
      method: 'DELETE',
    });
  },

  regenerateCode: async (id: number) => {
    return fetchApi<{ accessCode: string }>(`/organizations/${id}/regenerate-code`, {
      method: 'POST',
    });
  },

  updateSettings: async (id: number, settings: Record<string, SettingValue>) => {
    return fetchApi<{ settings: Record<string, SettingValue>; effectiveSettings: Record<string, EffectiveSetting> }>(`/organizations/${id}/settings`, {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  },

  resetSetting: async (id: number, key: string) => {
    return fetchApi<{ effectiveSettings: Record<string, EffectiveSetting> }>(`/organizations/${id}/settings/${key}`, {
      method: 'DELETE',
    });
  },
};

// Admin API (Admin only)
export const adminApi = {
  getAll: async () => {
    return fetchApi<{ admins: Admin[] }>('/admins');
  },

  getById: async (id: number) => {
    return fetchApi<{ admin: Admin }>(`/admins/${id}`);
  },

  create: async (data: { name: string; organizationId?: number; isSuperAdmin?: boolean }) => {
    return fetchApi<{ admin: Admin; generatedCode: string }>('/admins', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: { name?: string; organizationId?: number | null; isActive?: boolean }) => {
    return fetchApi<{ admin: Admin }>(`/admins/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  regenerateCode: async (id: number) => {
    return fetchApi<{ generatedCode: string }>(`/admins/${id}/regenerate`, {
      method: 'POST',
    });
  },
};

// Access Code API is no longer needed since each org/admin has their own code
// Keeping for backward compatibility but simplified
export const accessCodeApi = {
  getAll: async (filters?: { type?: string; isActive?: boolean; organizationId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.organizationId) params.append('organizationId', String(filters.organizationId));
    const query = params.toString() ? `?${params.toString()}` : '';
    return fetchApi<{ accessCodes: AccessCode[] }>(`/access-codes${query}`);
  },

  getById: async (id: number) => {
    return fetchApi<{ accessCode: AccessCode }>(`/access-codes/${id}`);
  },

  create: async (data: { organizationId: number; description?: string; maxUses?: number | '' }) => {
    return fetchApi<{ accessCode: AccessCode; generatedCode: string }>('/access-codes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: number, data: { isActive?: boolean; description?: string; maxUses?: number | null }) => {
    return fetchApi<{ accessCode: AccessCode }>(`/access-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  regenerate: async (id: number) => {
    return fetchApi<{ accessCode: AccessCode; generatedCode: string }>(`/access-codes/${id}/regenerate`, {
      method: 'POST',
    });
  },
};

// Global Settings API (Admin only)
export const settingsApi = {
  getGlobal: async () => {
    return fetchApi<{ settings: Record<string, SettingValue> }>('/settings/global');
  },

  getGlobalWithMeta: async () => {
    return fetchApi<{ settings: GlobalSetting[] }>('/settings/global/meta');
  },

  updateGlobal: async (settings: Record<string, { type?: 'boolean' | 'number' | 'string' | 'option'; value: boolean | number | string }>) => {
    return fetchApi<{ settings: Record<string, SettingValue> }>('/settings/global', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  },

  upsertSetting: async (data: { key: string; value: boolean | number | string; type: 'boolean' | 'number' | 'string' | 'option'; options?: string[]; description?: string }) => {
    return fetchApi<{ setting: GlobalSetting }>('/settings/global/setting', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteSetting: async (key: string) => {
    return fetchApi(`/settings/global/${key}`, {
      method: 'DELETE',
    });
  },
};
